import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { ChevronDown, Mail, Bell, FileText, DollarSign, Users, TestTube } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const AdminTestingPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const { toast } = useToast();

  // Test Email Form
  const [testEmail, setTestEmail] = useState({
    to: "lance@blaqout.us",
    subject: "Test Email from Ascendry Admin",
    message: "This is a test email from the admin dashboard!"
  });

  // Test Notification Form
  const [testNotification, setTestNotification] = useState({
    type: "new_student_signup",
    userEmail: "test@example.com",
    firstName: "Test",
    lastName: "User"
  });

  // Test Invoice Form
  const [testInvoice, setTestInvoice] = useState({
    sessionId: "",
    mentorEmail: "lance@blaqout.us"
  });

  // Manual mentor booking notification
  const [bookingSessionId, setBookingSessionId] = useState<string>("");

  const handleTestEmail = async () => {
    setLoading("email");
    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: testEmail
      });

      if (error) throw error;

      toast({
        title: "Test Email Sent!",
        description: `Email sent successfully to ${testEmail.to}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestNotification = async () => {
    setLoading("notification");
    try {
      const { data, error } = await supabase.functions.invoke('send-admin-notification', {
        body: testNotification
      });

      if (error) throw error;

      toast({
        title: "Test Notification Sent!",
        description: "Admin notification sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestMentorEmail = async () => {
    setLoading("mentor-email");
    try {
      const { data, error } = await supabase.functions.invoke('test-mentor-email');

      if (error) throw error;

      toast({
        title: "Test Mentor Email Sent!",
        description: "Mentor application notification test sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestPayoutInvoice = async () => {
    if (!testInvoice.sessionId) {
      toast({
        title: "Error",
        description: "Please provide a session ID",
        variant: "destructive",
      });
      return;
    }

    setLoading("payout");
    try {
      const { data, error } = await supabase.functions.invoke('send-payout-invoice', {
        body: { sessionId: testInvoice.sessionId }
      });

      if (error) throw error;

      toast({
        title: "Test Payout Invoice Sent!",
        description: "Payout invoice sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestMentorNotification = async () => {
    setLoading("mentor-notification");
    try {
      const { data: mentors } = await supabase
        .from('mentors')
        .select('id')
        .limit(1);

      if (!mentors || mentors.length === 0) {
        toast({
          title: "Error",
          description: "No mentors found for testing",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('send-mentor-notification', {
        body: {
          type: 'payout_system_update',
          mentorId: mentors[0].id
        }
      });

      if (error) throw error;

      toast({
        title: "Test Mentor Notification Sent!",
        description: "Mentor notification sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleSendMentorBookingEmail = async () => {
    setLoading("mentor-booking");
    try {
      const sessionId = bookingSessionId || '176afd1a-cbd0-423e-ad56-c90127b8a4e3';
      
      const { data, error } = await supabase.functions.invoke('send-mentor-notification', {
        body: { 
          type: 'session_booked',
          sessionId: sessionId
        }
      });
      
      if (error) throw error;
      
      toast({ 
        title: "Mentor Booking Email Sent!", 
        description: `Notification sent to mentor for session ${sessionId}` 
      });
    } catch (error: any) {
      toast({ 
        title: "Error", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestSystemHealth = async () => {
    setLoading("system-health");
    try {
      const { data, error } = await supabase.functions.invoke('system-health');

      if (error) throw error;

      toast({
        title: "System Health Check Complete!",
        description: "All systems operational",
      });
    } catch (error: any) {
      toast({
        title: "System Health Warning",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestNotificationSystem = async () => {
    setLoading("notification-system");
    try {
      // Test creating a notification directly in the database first
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Create test notification directly
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          title: 'Direct Test Notification',
          message: 'This notification was created directly to test the system.',
          type: 'info',
          is_read: false
        })
        .select()
        .single();
      
      if (error) throw error;

      toast({
        title: "Notification System Test Complete!",
        description: "Test notification created successfully. Check your notification bell!",
      });
    } catch (error: any) {
      console.error('Notification system test failed:', error);
      toast({
        title: "Notification System Test Failed",
        description: error.message || "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleTestCompleteSessionAutomation = async () => {
    setLoading("complete-session");
    try {
      const { data, error } = await supabase.functions.invoke('complete-session');

      if (error) throw error;

      const result = data || { completedSessions: 0 };
      
      toast({
        title: "Session Completion Automation Test Complete!",
        description: `Processed ${result.completedSessions || 0} sessions. Check logs for payout invoice triggers.`,
      });
    } catch (error: any) {
      toast({
        title: "Session Automation Test Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card className="mb-6">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Admin Testing Panel
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Test Email Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <h4 className="font-semibold">Test Email System</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="test-email-to">To Email</Label>
                  <Input
                    id="test-email-to"
                    value={testEmail.to}
                    onChange={(e) => setTestEmail(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="test@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="test-email-subject">Subject</Label>
                  <Input
                    id="test-email-subject"
                    value={testEmail.subject}
                    onChange={(e) => setTestEmail(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleTestEmail} 
                    disabled={loading === "email"}
                    className="w-full"
                  >
                    {loading === "email" ? "Sending..." : "Send Test Email"}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="test-email-message">Message</Label>
                <Textarea
                  id="test-email-message"
                  value={testEmail.message}
                  onChange={(e) => setTestEmail(prev => ({ ...prev, message: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            {/* Test Notifications Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                <h4 className="font-semibold">Test Admin Notifications</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="test-notification-email">User Email</Label>
                  <Input
                    id="test-notification-email"
                    value={testNotification.userEmail}
                    onChange={(e) => setTestNotification(prev => ({ ...prev, userEmail: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="test-notification-first">First Name</Label>
                  <Input
                    id="test-notification-first"
                    value={testNotification.firstName}
                    onChange={(e) => setTestNotification(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="test-notification-last">Last Name</Label>
                  <Input
                    id="test-notification-last"
                    value={testNotification.lastName}
                    onChange={(e) => setTestNotification(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleTestNotification} 
                    disabled={loading === "notification"}
                    className="w-full"
                  >
                    {loading === "notification" ? "Sending..." : "Send Test Notification"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Test Invoice Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <h4 className="font-semibold">Test Payout Invoice</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="test-invoice-session">Session ID</Label>
                  <Input
                    id="test-invoice-session"
                    value={testInvoice.sessionId}
                    onChange={(e) => setTestInvoice(prev => ({ ...prev, sessionId: e.target.value }))}
                    placeholder="Enter completed session ID"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleTestPayoutInvoice} 
                    disabled={loading === "payout"}
                    className="w-full"
                  >
                    {loading === "payout" ? "Sending..." : "Send Test Invoice"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Test Functions */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <h4 className="font-semibold">Quick Test Functions</h4>
              </div>

              {/* Manually send mentor booking email */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="booking-session-id">Session ID (optional)</Label>
                  <Input
                    id="booking-session-id"
                    value={bookingSessionId}
                    onChange={(e) => setBookingSessionId(e.target.value)}
                    placeholder="Leave empty to use the latest Josh discovery call"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleSendMentorBookingEmail}
                    disabled={loading === "mentor-booking"}
                    variant="default"
                    className="w-full"
                  >
                    {loading === "mentor-booking" ? "Sending..." : "Send Mentor Booking Email"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Button 
                  onClick={handleTestMentorEmail} 
                  disabled={loading === "mentor-email"}
                  variant="outline"
                >
                  {loading === "mentor-email" ? "Sending..." : "Test Mentor Application Email"}
                </Button>
                <Button 
                  onClick={handleTestMentorNotification} 
                  disabled={loading === "mentor-notification"}
                  variant="outline"
                >
                  {loading === "mentor-notification" ? "Sending..." : "Test Mentor Notification"}
                </Button>
                <Button 
                  onClick={handleTestSystemHealth} 
                  disabled={loading === "system-health"}
                  variant="outline"
                >
                  {loading === "system-health" ? "Checking..." : "Test System Health"}
                </Button>
                <Button 
                  onClick={handleTestNotificationSystem} 
                  disabled={loading === "notification-system"}
                  variant="outline"
                >
                  {loading === "notification-system" ? "Testing..." : "Test Notification System"}
                </Button>
                <Button 
                  onClick={handleTestCompleteSessionAutomation} 
                  disabled={loading === "complete-session"}
                  variant="outline"
                >
                  {loading === "complete-session" ? "Processing..." : "Test Complete Session Automation"}
                </Button>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};